import Vue from 'vue'
import Vuex from 'vuex'
import { sha256 } from 'js-sha256'
import throttle from 'lodash/throttle'
import { postCall } from '@/backend'

Vue.use(Vuex)

// Changing the dbVersion wipes store from all clients. Only do this if you need to change the data structure.
const dbVersion = 3
const hmacKey = 'a8dQLFV3P7vFrTM2QLa5YFkJ7962oD2c'
// Prerender on locations loaded
const prerenderEventName = 'prerender-event'

const store = new Vuex.Store({
  strict: process.env.NODE_ENV !== 'production',
  state: {
    version: dbVersion,
    language: '',
    ui: {
      working: []
    },
    pages: [],
    images: {
      residence: 'miten-asuisit-mieluiten.jpg',
      profession: 'mika-on-unelma-alasi.jpg',
      values: 'mika-on-sinulle-tarkeaa.jpg',
      hobbies: 'millaisia-asioita-harrastat.jpg'
    },
    questions: { residency: [], profession: [], services: [], hobbies: [] },
    locations: [],
    realtors: [],
    points: [],
    results: [],
    winner: [],
    second: [],
    third: [],
    max: {
      residence: 2,
      profession: 2,
      services: 3,
      hobbies: 5
    },
    current: {
      position: 'residence',
      image: ''
    },
    contest: false
  },
  getters: {
    getResidences (state) {
      return state.questions.residency.map(residency => {
        return {
          id: residency.id,
          title: residency[state.language].title,
          location_ids: residency.location_ids.map(x => {
            return parseInt(x)
          })
        }
      })
    },
    getProfessions (state) {
      return state.questions.profession.map(profession => {
        return {
          id: profession.id,
          title: profession[state.language].title,
          location_ids: profession.location_ids.map(x => {
            return parseInt(x)
          })
        }
      })
    },
    getServices (state) {
      return state.questions.services.map(service => {
        return {
          id: service.id,
          title: service[state.language].title,
          location_ids: service.location_ids.map(x => {
            return parseInt(x)
          })
        }
      })
    },
    getHobbies (state) {
      return state.questions.hobbies.map(hobby => {
        return {
          id: hobby.id,
          title: hobby[state.language].title,
          location_ids: hobby.location_ids.map(x => {
            return parseInt(x)
          })
        }
      })
    },
    getSharedLocation: (state) => (slug) => {
      for (const l of state.locations) {
        if (slug === l.slug) {
          return {
            title: l[state.language].title,
            description: l[state.language].content,
            image: l.image,
            link: l[state.language].link,
            sponsor_image: l[state.language].sponsorImg,
            sponsor_alt: l[state.language].sponsorImgAlt,
            sponsor_text: l[state.language].sponsorText,
            sponsor_name: l[state.language].sponsorName
          }
        }
      }
      return ''
    },
    getWinner (state) {
      for (const l of state.locations) {
        if (state.winner === l.id) {
          const param = l.fi.title.toLowerCase()
          return {
            title: l[state.language].title,
            description: l[state.language].content,
            image: l.image,
            link: l[state.language].link,
            sponsor_image: l[state.language].sponsorImg,
            sponsor_alt: l[state.language].sponsorImgAlt,
            sponsor_text: l[state.language].sponsorText,
            sponsor_name: l[state.language].sponsorName,
            param: param
          }
        }
      }
      return ''
    },
    getSecond (state) {
      for (const l of state.locations) {
        if (state.second === l.id) {
          return {
            title: l[state.language].title,
            description: l[state.language].content,
            image: l.image,
            link: l[state.language].link
          }
        }
      }
      return ''
    },
    getThird (state) {
      for (const l of state.locations) {
        if (state.third === l.id) {
          return {
            title: l[state.language].title,
            description: l[state.language].content,
            image: l.image,
            link: l[state.language].link
          }
        }
      }
      return ''
    },
    getRealtors (state) {
      return state.realtors.map(realtor => {
        return {
          logoAlt: realtor.logoAlt,
          logoUrl: realtor.logoUrl,
          name: realtor[state.language].name,
          link: realtor[state.language].link
        }
      })
    },
    getLocations (state) {
      return state.locations.map(location => {
        return {
          id: location.id,
          title: location[state.language].title,
          description: location[state.language].content,
          link: location[state.language].link,
          image: location.image
        }
      })
    },
    getPage: (state) => (slug) => {
      const page = state.pages.find(page => page.slug === slug)
      if (page && page[state.language]) {
        return page[state.language]
      }
      return ''
    },
    getCurrent (state) {
      return state.current.position
    },
    isWorking (state) {
      return state.ui.working.length > 0
    },
    isResidence (state) {
      return state.current.position === 'residence'
    },
    isProfession (state) {
      return state.current.position === 'profession'
    },
    isValues (state) {
      return state.current.position === 'values'
    },
    isHobbies (state) {
      return state.current.position === 'hobbies'
    },
    isResult (state) {
      return state.current.position === 'result'
    }
  },
  mutations: {
    initialiseStore (state, payload) {
      const stateJson = payload.store
      // Check if the ID exists
      if (stateJson) {
        // Validate store
        if (sha256.hmac(hmacKey, stateJson) === payload.hmac) {
          const store = JSON.parse(stateJson)
          // Replace the state object with the stored item
          if (store.version === dbVersion) {
            console.debug('Restore from localstorage')
            this.replaceState(
              Object.assign(state, store)
            )
          } else {
            console.warn('Version mismatch')
          }
        } else {
          console.warn('Invalid hmac')
        }
      }
    },
    language (state, payload) {
      state.language = payload.language
    },
    pages (state, payload) {
      state.pages = payload.pages
    },
    questions (state, payload) {
      state.questions = payload.questions
    },
    locations (state, payload) {
      state.locations = payload.locations
      document.dispatchEvent(new Event(prerenderEventName))
    },
    realtors (state, payload) {
      state.realtors = payload.realtors
    },
    working (state, payload) {
      const check = state.ui.working.indexOf(payload.id)
      if (payload.start) {
        if (check === -1) {
          state.ui.working.push(payload.id)
        }
      } else {
        if (check !== -1) {
          state.ui.working.splice(check, 1)
        }
      }
    },
    setPos (state, payload) {
      state.current.position = payload
    },
    addPoints (state, payload) {
      console.debug(payload)
      state.points.push(...payload)
    },
    resetPoints (state) {
      state.points = []
    },
    contestEntered (state) {
      state.contest = true
    },
    setResults (state) {
      const uniqueCount = state.points
      const points = uniqueCount.reduce((acum, cur) => Object.assign(acum, { [cur]: (acum[cur] | 0) + 1 }), {})
      const entries = Object.entries(points)
      const sorted = entries.sort((a, b) => b[1] - a[1])
      state.results = sorted
      state.winner = parseInt(sorted[0][0])
      state.second = parseInt(sorted[1][0])
      state.third = parseInt(sorted[2][0])
    }
  },
  actions: {
    resetMachine ({ commit }) {
      commit('resetPoints')
    },
    setResults ({ commit }) {
      commit('setResults')
    },
    submitForm (context, payload) {
      console.debug(payload)
      postCall('/submission', payload, 'submission').then(response => {
        if (response.data.is_valid) {
          context.commit('contestEntered')
        }
      })
    }
  },
  modules: {
  }
})

// Subscribe to store updates, update at most once every second.
store.subscribe(throttle((mutation, state) => {
  if (mutation.type !== 'initialiseStore') {
    const stateJson = JSON.stringify({ ...state, ui: undefined })
    const hmac = sha256.hmac(hmacKey, stateJson)
    // Store the state object as a JSON string
    localStorage.setItem('store', stateJson)
    localStorage.setItem('hmac', hmac)
  }
}, 1000))

export default store
